import {
  IDrawOverlaysAddParams,
  IResult,
  RESULT_STATUS,
  IDrawOverlaysDeleteParams,
  IPointSymbol,
  IPolylineSymbol,
  IPolygonSymbol,
  GEO_TYPE,
  DefaultPoint,
  DefaultPolygon,
  DefaultPolyline,
} from "@/types/cesiumMap";

import {
  ScreenSpaceEventHandler,
  defined,
  ScreenSpaceEventType,
  Color,
  HeightReference,
  Entity,
  ArcType,
  PolygonHierarchy,
  CallbackProperty,
  BillboardGraphics,
  Cartesian2,
  CustomDataSource,
  Cartesian3,
} from "cesium";

import * as Cesium from "cesium";

import * as Utils from "@/plugin/gis-viewer/cesiumUtils";
import Viewer from "cesium/Source/Widgets/Viewer/Viewer";

export default class DrawOverlays {
  private static Instances = new Map<string, DrawOverlays>();

  private viewer!: any;
  private eventHandler: any;
  private drawLayer: any; //绘制覆盖物的图层
  private drawEntities: Entity.ConstructorOptions[];
  private positions: Array<any>;

  constructor(viewer: any) {
    this.viewer = viewer;
    this.drawEntities = new Array<Entity.ConstructorOptions>();
    this.positions = new Array<any>();
    this.drawLayer = new CustomDataSource("draw-overlay-layer"); //默认图层，后续可用于数据分层
  }

  public static GetInstance(viewer: any): DrawOverlays {
    const elementId = viewer.container;
    const draw = DrawOverlays.Instances.get(elementId);
    if (draw instanceof DrawOverlays) {
      return draw;
    } else {
      const newDraw = new DrawOverlays(viewer);
      DrawOverlays.Instances.set(elementId, newDraw);
      return newDraw;
    }
  }

  /**
   * startDrawOverlays
   * 鼠标点击绘制覆盖物
   */
  public async startDrawOverlays(
    params: IDrawOverlaysAddParams
  ): Promise<IResult> {
    //初始化鼠标事件处理器
    if (!this.eventHandler) {
      this.eventHandler = new ScreenSpaceEventHandler(this.viewer.canvas);
    }

    //判断绘制类型
    const draw_type = params.drawType;
    if (draw_type == "point") {
      return this._drawPoint(params);
    } else if (draw_type == "polyline") {
      return this._drawPolyline(params);
    } else if (draw_type == "polygon") {
      return this._drawPolygon(params);
    } else if (draw_type == "rectangle") {
      //@todo
    } else if (draw_type == "circle") {
      //@todo
    }
    return {
      status: RESULT_STATUS.FAILED,
      message: "drawType error!",
    };
  }

  /**
   * stopDrawOverlays
   * 停止绘制
   */
  public async stopDrawOverlays(): Promise<IResult> {
    if (this.eventHandler) {
      this.eventHandler = this.eventHandler && this.eventHandler.destroy();
    }
    return {
      status: RESULT_STATUS.SUCCESS,
    };
  }

  /**
   * deleteDrawOverlays
   * 删除绘制物
   */
  public async deleteDrawOverlays(
    params: IDrawOverlaysDeleteParams
  ): Promise<IResult> {
    const types = params.types || [];
    const ids = params.ids || [];
    const that = this;
    const both: boolean = ids.length > 0 && types.length > 0;
    const draw_entities = that.drawEntities.filter(
      (entity: Entity.ConstructorOptions) => {
        // id+type
        if (both) {
          if (
            ids.indexOf(entity.id || "") >= 0 &&
            types.indexOf(entity.name || "") >= 0
          ) {
            that.viewer.entities.remove(entity);
            return false;
          }
          return true;
        }
        // id或type
        if (
          ids.indexOf(entity.id || "") >= 0 ||
          types.indexOf(entity.name || "") >= 0
        ) {
          that.viewer.entities.remove(entity);
          return false;
        }
        return true;
      }
    );
    that.drawEntities = draw_entities;
    return {
      status: RESULT_STATUS.SUCCESS,
    };
  }

  //绘制点图元
  private async _drawPoint(params: IDrawOverlaysAddParams): Promise<IResult> {
    //@notice 暂时只绘制图元
    //鼠标左击开始绘制
    const viewer = this.viewer;
    const eventHandler = this.eventHandler;
    const that = this;
    const resultPromise = new Promise<IResult>((resolve) => {
      eventHandler.setInputAction(function (event: any) {
        //获取鼠标左击地图坐标
        const pickedPosition = Utils.pickPositionFromScreen(
          event.position,
          viewer
        );
        if (defined(pickedPosition)) {
          //添加点图元到图上
          const entity = that._addPoint2Map(pickedPosition, params);
          that.drawEntities.push(entity);
          //结束鼠标点击事件
          that._unRegisterEvents();
          //点位信息
          let ray = viewer.camera.getPickRay(event.position);
          let cartesian = viewer.scene.globe.pick(ray, viewer.scene);
          let cartographic = Cesium.Cartographic.fromCartesian(cartesian);
          let lng = Cesium.Math.toDegrees(cartographic.longitude); //经度值
          let lat = Cesium.Math.toDegrees(cartographic.latitude); //纬度值
          let mapPosition = { x: lng, y: lat, z: cartographic.height }; //cartographic.height的值为地形高度。
          console.log(mapPosition);
          if (params.callback) {
            params.callback(mapPosition);
          }
          resolve({
            status: RESULT_STATUS.SUCCESS,
            message: "draw a point on map",
          });
        }
      }, ScreenSpaceEventType.LEFT_CLICK);
    });
    return resultPromise;
  }

  //绘制线
  private async _drawPolyline(
    params: IDrawOverlaysAddParams
  ): Promise<IResult> {
    //鼠标左击开始绘制
    const viewer = this.viewer;
    const eventHandler = this.eventHandler;
    const that = this;
    const postions = new Array<any>();
    const arr = new Array<any>();
    let tmpLine: Entity.ConstructorOptions; //一条动态绘制线
    const resultPromise = new Promise<IResult>((resolve) => {
      //监听鼠标左击事件
      eventHandler.setInputAction(function (event: any) {
        //获取地图坐标
        const pickedPosition = Utils.pickPositionFromScreen(
          event.position,
          viewer
        );
        if (defined(pickedPosition)) {
          postions.push(pickedPosition);
        }
      }, ScreenSpaceEventType.LEFT_CLICK);

      //监听鼠标移动事件
      eventHandler.setInputAction(function (event: any) {
        //绘制上一个点和鼠标位置之间的连线
        if (postions.length > 0) {
          //获取地图坐标
          const pickedPosition = Utils.pickPositionFromScreen(
            event.endPosition,
            viewer
          );
          if (defined(pickedPosition)) {
            //更新动态线坐标
            if (!defined(tmpLine) && postions.length == 1) {
              postions.push(pickedPosition);
              tmpLine = that._addLine2Map(params, postions);
            }
            if (defined(tmpLine)) {
              postions.pop();
              postions.push(pickedPosition);
            }
          }
        }
      }, ScreenSpaceEventType.MOUSE_MOVE);

      //监听鼠标右击事件
      eventHandler.setInputAction(function (event: any) {
        //弹出最后一个点，并结束绘制
        postions.pop();
        // console.log(postions, "点位信息");

        if (postions.length > 1) {
          that.drawEntities.push(tmpLine);
        }
        that._unRegisterEvents();
        if (params.callback) {
          params.callback(that.drawEntities);
        }
        //获取线的坐标
        that.drawEntities.forEach((item) => {
          if (item.polyline) {
            console.log(item.polyline, 1);

            console.log(item.polyline.positions, 2);

            let data = (item.polyline.positions as CallbackProperty).getValue(
              that.viewer.clock.currentTime
            );
            // console.log(data, "点位信息2");
          }
        });

        postions.forEach((item: Cartesian3) => {
          let cartographic = Cesium.Cartographic.fromCartesian(item);
          let lng = Cesium.Math.toDegrees(cartographic.longitude); //经度值
          let lat = Cesium.Math.toDegrees(cartographic.latitude); //纬度值
          let mapPosition = { x: lng, y: lat, z: cartographic.height }; //cartographic.height的值为地形高度。
          arr.push(mapPosition);
        });
        console.log(JSON.stringify(arr), 22222111);

        resolve({
          status: RESULT_STATUS.SUCCESS,
          message: "draw a line on map",
          // data: that.drawEntities,
          data: JSON.stringify(arr),
        });
      }, ScreenSpaceEventType.RIGHT_CLICK);

      //监听鼠标左键双击事件
      eventHandler.setInputAction(function (event: any) {
        //删除临时线，并结束绘制
        postions.pop();
        if (postions.length > 1) {
          that.drawEntities.push(tmpLine);
        }
        that._unRegisterEvents();
        if (params.callback) {
          // params.callback(that.drawEntities);
        }
        resolve({
          status: RESULT_STATUS.SUCCESS,
          message: "draw a line on map",
          // data: that.drawEntities,
        });
      }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    });
    return resultPromise;
  }

  //绘制多边形
  private async _drawPolygon(params: IDrawOverlaysAddParams): Promise<IResult> {
    //鼠标左击开始绘制
    const viewer = this.viewer;
    const eventHandler = this.eventHandler;
    const that = this;
    const postions = new Array<any>();
    const arr = new Array<any>();
    let tmpPolygon: Entity.ConstructorOptions; //动态绘制面
    let tmpLine: Entity.ConstructorOptions; //动态线
    const resultPromise = new Promise<IResult>((resolve) => {
      //监听鼠标左击事件
      eventHandler.setInputAction(function (event: any) {
        //获取地图坐标
        const pickedPosition = Utils.pickPositionFromScreen(
          event.position,
          viewer
        );
        if (defined(pickedPosition)) {
          postions.push(pickedPosition);
        }
      }, ScreenSpaceEventType.LEFT_CLICK);

      //监听鼠标移动事件
      eventHandler.setInputAction(function (event: any) {
        //绘制上一个点和鼠标位置之间的连线
        if (postions.length > 0) {
          //获取地图坐标
          const pickedPosition = Utils.pickPositionFromScreen(
            event.endPosition,
            viewer
          );
          //更新动态面
          if (!defined(tmpPolygon) && postions.length == 1) {
            postions.push(pickedPosition);
            tmpPolygon = that._addPolygon2Map(params, postions);
          }
          if (defined(tmpPolygon)) {
            postions.pop();
            postions.push(pickedPosition);
          }
          //两个点时添加一条线
          if (postions.length == 2 && !defined(tmpLine)) {
            tmpLine = that._addLine2Map(
              {
                symbol: (params.symbol as IPolygonSymbol)?.outline,
                drawType: GEO_TYPE.POLYLINE,
                type: "temp-line-while-draw",
              },
              postions
            );
          }
          if (postions.length > 2) {
            that.viewer.entities.remove(tmpLine);
          }
        }
      }, ScreenSpaceEventType.MOUSE_MOVE);

      //监听鼠标右击事件
      eventHandler.setInputAction(function (event: any) {
        //删除临时线，弹出最后一个点，添加最终的绘制结果，并结束绘制
        that.viewer.entities.remove(tmpLine);
        postions.pop();
        if (postions.length > 2) {
          that.drawEntities.push(tmpPolygon);
        }
        that._unRegisterEvents();
        if (params.callback) {
          // params.callback(that.drawEntities);
        }
        postions.forEach((item: Cartesian3) => {
          let cartographic = Cesium.Cartographic.fromCartesian(item);
          let lng = Cesium.Math.toDegrees(cartographic.longitude); //经度值
          let lat = Cesium.Math.toDegrees(cartographic.latitude); //纬度值
          let mapPosition = { x: lng, y: lat, z: cartographic.height }; //cartographic.height的值为地形高度。
          arr.push(mapPosition);
        });
        console.log(JSON.stringify(arr), "点位");

        resolve({
          status: RESULT_STATUS.SUCCESS,
          message: "draw a polygon on map",
          // data: that.drawEntities,
          data: JSON.stringify(arr),
        });
      }, ScreenSpaceEventType.RIGHT_CLICK);

      //监听鼠标左键双击事件
      eventHandler.setInputAction(function (event: any) {
        //删除临时线，弹出最后一个点，添加最终的绘制结果，并结束绘制
        that.viewer.entities.remove(tmpLine);
        postions.pop();
        if (postions.length > 2) {
          that.drawEntities.push(tmpPolygon);
        }
        that._unRegisterEvents();
        if (params.callback) {
          // params.callback(that.drawEntities);
        }
        resolve({
          status: RESULT_STATUS.SUCCESS,
          message: "draw a polygon on map",
          // data: that.drawEntities,
        });
      }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    });
    return resultPromise;
  }

  //将绘制点添加到地图上
  private _addPoint2Map(
    position: any,
    params: IDrawOverlaysAddParams
  ): Entity.ConstructorOptions {
    //parse symbol of point
    const symbol = (params.symbol as IPointSymbol) || DefaultPoint;
    //图标
    let billboard: any;
    // @notice 暂时不搞这个
    // let imageSubRegion_: any;
    // if (symbol.imgBgSize) {
    //   imageSubRegion_ = new BoundingRectangle(
    //     undefined,
    //     undefined,
    //     symbol.imgBgSize[0],
    //     symbol.imgBgSize[1]
    //   );
    // }
    if (symbol.url) {
      billboard = new BillboardGraphics({
        image: symbol.url as string,
        pixelOffset: new Cartesian2(symbol.xoffset, symbol.yoffset),
        show: true,
        // eyeOffset:,
        horizontalOrigin: Utils.getHAnchorFromString(symbol.hAnchor),
        verticalOrigin: Utils.getVAnchorFromString(symbol.vAnchor),
        rotation: symbol.rotation,
        width: symbol.width,
        height: symbol.height,
        // imageSubRegion: imageSubRegion_,
        // disableDepthTestDistance: Number.POSITIVE_INFINITY,
      });
    }
    //图元
    let point: any;
    if (symbol.size && symbol.size > 0) {
      point = {
        color: Utils.getColorFromArray(symbol?.color),
        pixelSize: symbol?.size,
        outlineColor: Utils.getColorFromArray(symbol?.outline?.color),
        outlineWidth: symbol.outline?.width,
        disableDepthTestDistance: Number.POSITIVE_INFINITY, //深度检测，用于遮挡和透视
        // heightReference: HeightReference.CLAMP_TO_GROUND,//贴合地面
      };
    }
    return this.viewer.entities.add({
      id: params.id,
      name: params.type || "draw-point",
      position: position,
      show: true,
      point: point,
      billboard: billboard,
    });
  }

  private _addLine2Map(
    params: IDrawOverlaysAddParams,
    positions: Array<any>
  ): Entity.ConstructorOptions {
    //parse symbol of polyline
    const symbol = (params.symbol as IPolylineSymbol) || DefaultPolyline;
    return this.viewer.entities.add({
      id: params.id,
      name: params.type || "draw-polyline",
      polyline: {
        // arcType: ArcType.NONE,
        positions: new CallbackProperty(function () {
          return positions;
        }, false),
        width: symbol.width || 1,
        material: Utils.getColorFromArray(symbol.color),
        distanceDisplayCondition: Number.POSITIVE_INFINITY, //显示折线的相机距离
        depthFailMaterial: Utils.getColorFromArray(symbol.color), //地下部分的线颜色
        clampToGround: true, //贴合地面之上，防止被底图遮挡
        zIndex: 1,
      },
    });
  }

  private _addPolygon2Map(
    params: IDrawOverlaysAddParams,
    positions: Array<any>
  ): Entity.ConstructorOptions {
    //parse symbol of polygon
    const symbol = (params.symbol as IPolygonSymbol) || DefaultPolygon;
    const hasOutline = symbol.outline != undefined;
    return this.viewer.entities.add({
      id: params.id,
      name: params.type || "draw-polygon",
      polygon: {
        height: 5, //多边形高度默认5
        outline: hasOutline,
        outlineColor: Utils.getColorFromArray(symbol.outline?.color),
        outlineWidth: symbol.outline?.width || 1, // windowsOS上不会起作用，默认是1 @link https://github.com/CesiumGS/cesium/issues/40
        material: Utils.getColorFromArray(symbol.color),
        // arcType: ArcType.NONE,
        hierarchy: new CallbackProperty(function () {
          return new PolygonHierarchy(positions);
        }, false),
        // distanceDisplayCondition: Number.POSITIVE_INFINITY, //显示折线的相机距离
        // zIndex: 10,
      },
    });
  }

  private _unRegisterEvents() {
    this.eventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
    this.eventHandler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
    this.eventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    this.eventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
  }
}
